import type { FuseResultMatch } from 'fuse.js'

/**
 * Categories for grouping search results.
 * Used for ranking - lower priority number = higher rank in search results.
 */
export enum SEARCH_RESULT_CATEGORY {
  HIGH_RISK = 'high_risk',
  CARE_OPTIONS = 'care_options',
  TOOLKIT = 'toolkit',
  RESOURCES = 'resources',
  EVENTS = 'events',
  COVERAGE = 'coverage',
  OTHER_BENEFITS = 'other_benefits',
  ACCOUNT = 'account',
  FAQS = 'faqs',
  GENERAL_CNT_SUPPORT = 'general_cnt_support',
}

/**
 * Type identifier for searchable item metadata.
 * Determines how the search result is rendered and what action occurs on interaction.
 */
export enum SEARCH_RESULT_TYPE {
  /** Navigable route - clicking navigates to the page */
  STANDARD = 'standard',
  /** Route with parameters - clicking navigates with query params */
  PARAM_ROUTE = 'paramRoute',
  /** Modal - clicking opens an informational modal */
  MODAL = 'modal',
  /** External URL - opens in browser/new tab */
  EXTERNAL_URL = 'externalUrl',
  /** FAQ item - accordion-style expand/collapse */
  FAQ = 'faq',
}

/**
 * Unique identifiers for each searchable item.
 * Used for type-safe references in eligibility checks, analytics, etc.
 */
export enum SEARCHABLE_ITEM_ID {
  // Triage
  ADULT_TRIAGE = 'adult-triage',
  CHILD_TRIAGE = 'child-triage',
  FORKED_TRIAGE = 'forked-triage',

  // Programs (DPLs)
  COACHING = 'coaching',
  PARENTING_COACHING = 'parenting-coaching',
  COUPLES_THERAPY = 'couples-therapy',
  FAMILY_THERAPY = 'family-therapy',
  MEDICATION_CONSULT = 'medication-consult',
  RENEW_AUD = 'renew-aud',

  // Complex Care / External
  COMPLEX_CARE = 'complex-care',
  DBT = 'dbt',
  IOP = 'iop',
  PHP = 'php',
  RESIDENTIAL = 'residential',
  INPATIENT = 'inpatient',

  // Toolkits
  TOOLKIT_RELATIONSHIPS = 'toolkit-relationships',
  TOOLKIT_WORK = 'toolkit-work',
  TOOLKIT_FAMILY = 'toolkit-family',
  TOOLKIT_NEURODIVERSITY = 'toolkit-neurodiversity',
  TOOLKIT_HEALTH_CHALLENGES = 'toolkit-health-challenges',
  TOOLKIT_WOMENS_HEALTH = 'toolkit-womens-health',
  TOOLKIT_PARENTING = 'toolkit-parenting',
  TOOLKIT_VALUES = 'toolkit-values',
  TOOLKIT_TRAUMA = 'toolkit-trauma',

  // Care Tab
  CARE_TAB = 'care-tab',
  SESSIONS = 'sessions',

  // Library
  LIBRARY = 'library',
  EVENTS = 'events',
  MEDITATION = 'meditation',

  // Benefits / Wellness
  BENEFITS = 'benefits',
  WELLNESS_CHECK_IN = 'wellness-check-in',

  // Account
  ACCOUNT = 'account',
  SETTINGS = 'settings',
  SESSION_HISTORY = 'session-history',
  COVERAGE_INFO = 'coverage-info',
  HEALTH_PLAN_PAYMENT = 'health-plan-payment',
  PRIVACY_LEGAL = 'privacy-legal',
  CLINICAL_DATA_SHARING = 'clinical-data-sharing',
  ADD_DEPENDENT = 'add-dependent',
  CHANGE_EMAIL = 'change-email',
  MFA = 'mfa',
  PHONE_NUMBER = 'phone-number',
  LOCATION_TIMEZONE = 'location-timezone',
  DISPLAY_LANGUAGE = 'display-language',
  NOTIFICATIONS = 'notifications',
  TRANSFER_ACCOUNT = 'transfer-account',
  ADD_HEALTH_PLAN = 'add-health-plan',
  ADD_PAYMENT_METHOD = 'add-payment-method',
  LATE_CANCELLATION_PAYMENT = 'late-cancellation-payment',
  FORGOT_PASSWORD = 'forgot-password',

  // External Links
  LYRA_APP = 'lyra-app',

  // CNT Support
  CONTACT_CN = 'contact-cn',

  // FAQs
  FAQS = 'faqs',
}

/**
 * Base interface for all searchable item metadata.
 */
interface BaseSearchableItemMetadata {
  /** Unique identifier for the searchable item */
  id: SEARCHABLE_ITEM_ID
  /** Display name shown in search results */
  displayName: string
  /** Keywords for fuzzy search matching */
  keywords: string[]
  /** Category for group-level ranking */
  category: SEARCH_RESULT_CATEGORY
  /** Priority within category (lower = higher rank). Used for tie-breaking. */
  priority?: number
  /**
   * When true, requires a stricter match (lower threshold) to appear in results.
   * Used for sensitive programs where vague searches should not surface the item.
   * Standard fuzzy search uses a higher threshold.
   */
  strictMatchOnly?: boolean
}

/**
 * Standard route that maps directly to a route path.
 * Web uses the path directly, mobile maps to screens via URL_PATH_TO_SCREEN.
 */
export interface StandardRouteMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.STANDARD
  /** Route path (e.g., '/secure/index/search-care') */
  route: string
}

/**
 * Route that requires additional parameters (e.g., routes with query params).
 */
export interface ParamRouteMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.PARAM_ROUTE
  /** Base route path */
  route: string
  /** Route parameters to append */
  params: Record<string, string | undefined>
}

/**
 * Modal item - clicking opens an informational modal.
 */
export interface ModalItemMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.MODAL
}

/**
 * External URL that opens in browser/new tab.
 * Used for oncehub links, microsites, etc.
 */
export interface ExternalUrlMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.EXTERNAL_URL
  url: string
}

/**
 * FAQ item with question and answer for accordion display.
 * Uses string ID (not enum) since FAQs are dynamically generated from customer data.
 */
export interface FAQItemMetadata extends Omit<BaseSearchableItemMetadata, 'id'> {
  /** Dynamic FAQ ID in format 'faq-{slug}' */
  id: string
  type: SEARCH_RESULT_TYPE.FAQ
  /** The answer content (displayName serves as the question) */
  answer: string
}

/**
 * Union of all searchable item types.
 */
export type SearchableItemMetadata =
  | StandardRouteMetadata
  | ParamRouteMetadata
  | ModalItemMetadata
  | ExternalUrlMetadata
  | FAQItemMetadata

/**
 * Type for category priority configuration.
 * Maps each search category to a priority number (lower = higher priority).
 */
export type CategoryPriorityConfig = Record<SEARCH_RESULT_CATEGORY, number>

/**
 * Default category priority for ranking search results.
 * Lower number = higher priority (shown first when scores are equal).
 */
export const DEFAULT_CATEGORY_PRIORITY: CategoryPriorityConfig = {
  [SEARCH_RESULT_CATEGORY.HIGH_RISK]: 1,
  [SEARCH_RESULT_CATEGORY.CARE_OPTIONS]: 2,
  [SEARCH_RESULT_CATEGORY.TOOLKIT]: 3,
  [SEARCH_RESULT_CATEGORY.RESOURCES]: 4,
  [SEARCH_RESULT_CATEGORY.EVENTS]: 5,
  [SEARCH_RESULT_CATEGORY.COVERAGE]: 6,
  [SEARCH_RESULT_CATEGORY.OTHER_BENEFITS]: 7,
  [SEARCH_RESULT_CATEGORY.ACCOUNT]: 8,
  [SEARCH_RESULT_CATEGORY.FAQS]: 9,
  [SEARCH_RESULT_CATEGORY.GENERAL_CNT_SUPPORT]: 10,
}

/**
 * Result from universal search, including the matched item and relevance score.
 */
export interface UniversalSearchResult {
  /** The matched searchable item metadata */
  item: SearchableItemMetadata
  /** Relevance score from Fuse.js (0 = perfect match, 1 = worst match) */
  score: number
  /** Match information indicating which fields matched (when includeMatches is enabled) */
  matches?: ReadonlyArray<FuseResultMatch>
}

// Re-export FuseResultMatch from fuse.js for convenience
export type { FuseResultMatch }

/**
 * Options for the debounced universal search hook.
 */
export interface UseUniversalSearchDebouncedOptions {
  /** Search query string (raw input, will be debounced internally) */
  query: string
  /** Maximum number of results to return */
  limit?: number
  /** Debounce delay in milliseconds */
  debounceMs?: number
}

/**
 * Debug info about eligible pages for troubleshooting search issues.
 */
export interface EligiblePagesDebugInfo {
  totalCount: number
  /** Count of strict match pages */
  strictCount: number
  /** Count of standard match pages */
  standardCount: number
  pageNames: string[]
}

/**
 * Result from the debounced universal search hook.
 */
export interface UseUniversalSearchDebouncedResult {
  /** Search results based on the debounced query */
  results: UniversalSearchResult[]
  /** The current debounced query being searched */
  debouncedQuery: string
  /** Whether a search is pending (query changed but debounce hasn't fired yet) */
  isPending: boolean
  /** Debug info about eligible pages */
  eligiblePagesDebugInfo: EligiblePagesDebugInfo
}
